import { useEffect, useState } from "react"
import { Button, Card, Checkbox, DatePicker, Divider, Flex, Form, Input, InputNumber, Modal, Radio, Segmented } from "antd"
import { DeleteOutlined, InfoCircleOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Text } from "ui/Typography"
import CustomDrawer from "ui/Drawer"
import { useDevice } from "hooks/useDevice"
import { spacings } from "themes/variables"
import { APPLIES_ON_PRIVATE, PRICE_DIRECTION, PRICE_TYPE, PRICE_TYPE_OPTIONS } from "../../../constants/pricingConstants"
import { findConflictingSeasonalAdjustment, hasSelfOverlappingRanges } from "../../../helpers/adjustmentConflicts"
import { isServerId } from "../../../mappers/toApiPayload"
import styles from "./AddPrivateSeasonalAdjustmentModal.module.css"

const { RangePicker } = DatePicker

const AddPrivateSeasonalAdjustmentModal = ({ open, editingItem, defaultScheduleRanges = [], existingAdjustments = [], onCancel, onSave }) => {
  const { t } = useTranslation()
  const { isMobile } = useDevice()
  const [form] = Form.useForm()
  // All Year experiences have no season ranges, so "Default Schedule" has nothing
  // to apply to — those experiences only get Custom Dates.
  const hasDefaultSchedule = defaultScheduleRanges.length > 0
  const initialAppliesOn = hasDefaultSchedule ? APPLIES_ON_PRIVATE.DEFAULT_SCHEDULE : APPLIES_ON_PRIVATE.CUSTOM_DATES
  const [appliesOn, setAppliesOn] = useState(initialAppliesOn)
  const [dateRanges, setDateRanges] = useState([{ id: Date.now(), value: null }])
  // Inline date conflict: either the name of a clashing adjustment, or "" when this
  // adjustment's own ranges overlap each other.
  const [dateConflict, setDateConflict] = useState(null)
  const [isRepeatEachYear, setIsRepeatEachYear] = useState(true)
  const [direction, setDirection] = useState(PRICE_DIRECTION.INCREASE)
  const [priceType, setPriceType] = useState(PRICE_TYPE.PERCENT)

  useEffect(() => {
    if (open && editingItem) {
      form.setFieldsValue({ name: editingItem.name, priceValue: editingItem.priceValue })
      // An adjustment saved while the experience was still seasonal can carry
      // DEFAULT_SCHEDULE — fall back to Custom Dates once the season ranges are gone.
      setAppliesOn(hasDefaultSchedule ? editingItem.appliesOn || initialAppliesOn : APPLIES_ON_PRIVATE.CUSTOM_DATES)
      setDateRanges(editingItem.dateRanges?.length ? editingItem.dateRanges : [{ id: Date.now(), value: null }])
      setIsRepeatEachYear(editingItem.isRepeatEachYear ?? true)
      setDirection(editingItem.direction || PRICE_DIRECTION.INCREASE)
      setPriceType(editingItem.priceType || PRICE_TYPE.PERCENT)
    } else if (!open) {
      form.resetFields()
      setAppliesOn(initialAppliesOn)
      setDateRanges([{ id: Date.now(), value: null }])
      setIsRepeatEachYear(true)
      setDirection(PRICE_DIRECTION.INCREASE)
      setPriceType(PRICE_TYPE.PERCENT)
    }
    setDateConflict(null)
  }, [open, editingItem, form, initialAppliesOn, hasDefaultSchedule])

  const addDateRange = () => setDateRanges((prev) => [...prev, { id: Date.now(), value: null }])

  // A server-persisted range can't just be dropped from state — the backend
  // needs date_ranges_attributes[][_destroy]=true to delete it. So we mark it
  // and keep it (hidden from the UI). Client-temp ranges are simply removed.
  const removeDateRange = (id) =>
    setDateRanges((prev) =>
      prev.flatMap((range) => {
        if (range.id !== id) return [range]
        if (isServerId(range.id)) return [{ ...range, _destroy: true }]
        return []
      })
    )
  const handleAppliesOnChange = (value) => {
    setDateConflict(null)
    setAppliesOn(value)
  }

  const handleRepeatEachYearChange = (checked) => {
    setDateConflict(null)
    setIsRepeatEachYear(checked)
  }

  const updateDateRange = (id, value) => {
    setDateConflict(null)
    setDateRanges((prev) => prev.map((range) => (range.id === id ? { ...range, value } : range)))
  }

  // Ranges flagged for deletion stay in state (to send _destroy) but are hidden.
  const visibleDateRanges = dateRanges.filter((range) => !range._destroy)

  // Custom dates use the user-picked ranges (including _destroy flags so the
  // backend deletes removed ones); Default Schedule reuses the Scheduling-tab dates.
  const resolveDateRanges = () => {
    if (appliesOn === APPLIES_ON_PRIVATE.CUSTOM_DATES) return dateRanges
    if (appliesOn === APPLIES_ON_PRIVATE.DEFAULT_SCHEDULE) return defaultScheduleRanges
    return []
  }

  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      const adjustment = {
        ...(editingItem ? { id: editingItem.id } : {}),
        ...values,
        appliesOn,
        dateRanges: resolveDateRanges(),
        isRepeatEachYear,
        direction,
        priceType,
      }

      if (hasSelfOverlappingRanges(adjustment.dateRanges, isRepeatEachYear)) {
        setDateConflict({ isSelfOverlap: true })
        return
      }

      // Overlapping adjustments both apply to the same booking and their deltas stack,
      // so a clash is blocked here rather than silently double-charging later.
      const conflict = findConflictingSeasonalAdjustment(adjustment, existingAdjustments, defaultScheduleRanges)
      if (conflict) {
        setDateConflict({ name: conflict.name || "", hasDates: (conflict.dateRanges || []).length > 0 })
        return
      }

      onSave(adjustment)
    } catch {
      // AntD shows inline field errors
    }
  }

  const footer = (
    <Flex gap={spacings.S}>
      <Button shape="round" color="primary" variant="outlined" size="large" onClick={onCancel}>
        {t("experiences.pricing.adjustment_modal.cancel")}
      </Button>
      <Button shape="round" color="primary" variant="solid" size="large" onClick={handleSave}>
        {t("experiences.pricing.adjustment_modal.save")}
      </Button>
    </Flex>
  )

  const modalTitle = t(
    editingItem
      ? "experiences.pricing.seasonal_adjustment_modal.edit_title"
      : "experiences.pricing.seasonal_adjustment_modal.title"
  )

  const content = (
    <Form
      form={form}
      layout="vertical"
      requiredMark={(label, { required }) => (
        <>
          {label}
          {required && " *"}
        </>
      )}
    >
        <Card className={styles.contentCard}>
          <Flex vertical gap={spacings.XL}>
            <Form.Item
              name="name"
              label={t("experiences.pricing.adjustment_modal.name_label")}
              required
              rules={[{ required: true, message: t("experiences.pricing.adjustment_modal.name_required") }]}
              tooltip={t("experiences.pricing.adjustment_modal.name_tooltip")}
            >
              <Input placeholder={t("experiences.pricing.seasonal_adjustment_modal.name_placeholder")} size="large" />
            </Form.Item>

            <Flex vertical gap={spacings.M}>
              <Text fw={500} size="l">{t("experiences.pricing.adjustment_modal.applies_on")}</Text>

              <Radio.Group value={appliesOn} onChange={(e) => handleAppliesOnChange(e.target.value)}>
                <Flex gap={spacings.XL}>
                  {hasDefaultSchedule && (
                    <Radio value={APPLIES_ON_PRIVATE.DEFAULT_SCHEDULE}>
                      {t("experiences.pricing.seasonal_adjustment_modal.applies_on_default_schedule")}
                    </Radio>
                  )}
                  <Radio value={APPLIES_ON_PRIVATE.CUSTOM_DATES}>
                    {t("experiences.pricing.seasonal_adjustment_modal.applies_on_custom_dates")}
                  </Radio>
                </Flex>
              </Radio.Group>

              {dateConflict && (
                <Text size="s" className={styles.errorText}>
                  {dateConflict.isSelfOverlap
                    ? t("experiences.pricing.adjustment_modal.self_overlapping_ranges")
                    : t(
                        dateConflict.hasDates
                          ? "experiences.pricing.adjustment_modal.duplicate_dates"
                          : "experiences.pricing.adjustment_modal.duplicate_option",
                        { name: dateConflict.name }
                      )}
                </Text>
              )}

              {appliesOn === APPLIES_ON_PRIVATE.CUSTOM_DATES && (
                <Flex vertical gap={spacings.S}>
                  <Text fw={500} size="m">
                    {t("experiences.pricing.seasonal_adjustment_modal.select_dates_label")}
                  </Text>
                  <Flex vertical gap={spacings.S}>
                    {visibleDateRanges.map((range) => (
                      <Flex key={range.id} align="center" gap={spacings.S}>
                        <RangePicker
                          value={range.value}
                          size="large"
                          format="MMM D, YYYY"
                          placeholder={["mm/dd/yyyy", "mm/dd/yyyy"]}
                          className={styles.rangePicker}
                          onChange={(val) => updateDateRange(range.id, val)}
                        />
                        <Button
                          shape="circle"
                          size="large"
                          icon={<DeleteOutlined />}
                          onClick={() => removeDateRange(range.id)}
                          disabled={visibleDateRanges.length === 1}
                          className={styles.deleteBtn}
                        />
                      </Flex>
                    ))}
                    <Button type="link" className={styles.addMoreBtn} onClick={addDateRange}>
                      {t("experiences.pricing.seasonal_adjustment_modal.add_more")}
                    </Button>
                  </Flex>
                </Flex>
              )}

              {appliesOn === APPLIES_ON_PRIVATE.DEFAULT_SCHEDULE && (
                <Flex vertical gap={spacings.S}>
                  <Text fw={500} size="m">
                    {t("experiences.pricing.seasonal_adjustment_modal.season_dates_label", { defaultValue: "Season dates" })}
                  </Text>
                  {defaultScheduleRanges.length === 0 ? (
                    <Text type="secondary" className={styles.infoBoxText}>
                      {t("experiences.pricing.seasonal_adjustment_modal.no_season_dates", { defaultValue: "Set the season dates in the Scheduling tab first." })}
                    </Text>
                  ) : (
                    defaultScheduleRanges.map((range) => (
                      <RangePicker
                        key={range.id}
                        value={range.value}
                        size="large"
                        format="MMM D, YYYY"
                        disabled
                        className={styles.rangePicker}
                      />
                    ))
                  )}
                </Flex>
              )}

              {appliesOn === APPLIES_ON_PRIVATE.CUSTOM_DATES && (
                <Flex align="center" gap={spacings.XS} className={styles.infoBox}>
                  <InfoCircleOutlined className={styles.infoBoxIcon} />
                  <Text className={styles.infoBoxText}>
                    {t("experiences.pricing.seasonal_adjustment_modal.applies_on_info")}
                  </Text>
                </Flex>
              )}

              <Checkbox
                checked={isRepeatEachYear}
                onChange={(e) => handleRepeatEachYearChange(e.target.checked)}
              >
                {t("experiences.pricing.seasonal_adjustment_modal.repeat_each_year")}
              </Checkbox>
            </Flex>

            <Divider className={styles.divider} />

            <Flex vertical gap={spacings.M}>
              <Text fw={500} size="l">{t("experiences.pricing.adjustment_modal.price_adjustment")}</Text>
              <Radio.Group value={direction} onChange={(e) => setDirection(e.target.value)}>
                <Flex gap={spacings.XL}>
                  <Radio value={PRICE_DIRECTION.INCREASE}>{t("experiences.pricing.adjustment_modal.increase")}</Radio>
                  <Radio value={PRICE_DIRECTION.DECREASE}>{t("experiences.pricing.adjustment_modal.decrease")}</Radio>
                </Flex>
              </Radio.Group>
              <Flex vertical gap={spacings.XS}>
                <Flex align="center" gap={spacings.XS}>
                  <Form.Item
                    name="priceValue"
                    noStyle
                    rules={[{ required: true, message: t("experiences.pricing.adjustment_modal.value_required") }]}
                  >
                    <InputNumber className={styles.priceInput} size="large" min={0} max={priceType === PRICE_TYPE.PERCENT ? 100 : undefined} placeholder="0" />
                  </Form.Item>
                  <Segmented
                    value={priceType}
                    onChange={setPriceType}
                    options={PRICE_TYPE_OPTIONS}
                    size="large"
                    className={styles.segmented}
                  />
                </Flex>
                <Flex align="flex-start" gap={spacings.XS} className={styles.infoBox}>
                  <InfoCircleOutlined className={styles.infoBoxIcon} />
                  <Text className={styles.infoBoxText}>
                    {t("experiences.pricing.adjustment_modal.base_rate_info")}
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Card>
    </Form>
  )

  if (isMobile) {
    return (
      <CustomDrawer open={open} setOpen={() => onCancel()} title={modalTitle}>
        {content}
        {footer}
      </CustomDrawer>
    )
  }

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={modalTitle}
      width={1040}
      footer={footer}
    >
      {content}
    </Modal>
  )
}

AddPrivateSeasonalAdjustmentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  editingItem: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    priceValue: PropTypes.number,
    appliesOn: PropTypes.string,
    dateRanges: PropTypes.arrayOf(PropTypes.object),
    isRepeatEachYear: PropTypes.bool,
    direction: PropTypes.string,
    priceType: PropTypes.string,
  }),
  defaultScheduleRanges: PropTypes.arrayOf(PropTypes.object),
  onCancel: PropTypes.func.isRequired,
  existingAdjustments: PropTypes.arrayOf(PropTypes.object),
  onSave: PropTypes.func.isRequired,
}

AddPrivateSeasonalAdjustmentModal.defaultProps = {
  editingItem: null,
}

export default AddPrivateSeasonalAdjustmentModal
